import { useState } from "react";
import { trpc } from "@/lib/trpc";
import {
  Shield, CheckCircle, Clock, XCircle, Eye, ThumbsUp, ThumbsDown,
  User, MapPin, FileImage, AlertCircle, Search, Link2, Copy, Check,
  UserPlus, ChevronDown, ChevronUp, RefreshCw
} from "lucide-react";
import { toast } from "sonner";

// ─── Resend Verify Link Button ─────────────────────────────────────────────────
function ResendVerifyLinkButton({ repCode }: { repCode: string }) {
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const generateMutation = trpc.agentVerify.generateLink.useMutation({
    onSuccess: (data) => {
      setGeneratedLink(data.portalUrl);
      toast.success("Verification link regenerated and sent to Wibiz!");
    },
    onError: (e: any) => toast.error(e.message),
  });

  if (generatedLink) {
    return (
      <div className="flex items-center gap-1">
        <input
          readOnly
          value={generatedLink}
          className="text-xs border border-gray-200 rounded px-1.5 py-1 w-36 truncate bg-gray-50"
        />
        <button
          onClick={() => { navigator.clipboard.writeText(generatedLink); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
          className="p-1 text-gray-400 hover:text-[#84ab4a] transition-colors"
          title="Copy link"
        >
          {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={() => generateMutation.mutate({ repCode })}
      disabled={generateMutation.isPending}
      className="inline-flex items-center gap-1 bg-purple-50 hover:bg-purple-100 text-purple-700 text-xs px-3 py-1.5 rounded-lg font-medium transition-colors border border-purple-200 disabled:opacity-50"
    >
      {generateMutation.isPending ? (
        <div className="w-3 h-3 border border-purple-400 border-t-transparent rounded-full animate-spin" />
      ) : (
        <Link2 className="w-3 h-3" />
      )}
      Resend Verify Link
    </button>
  );
}

// ─── Re-trigger Identity Workflow Button ──────────────────────────────────────
function RetriggerWorkflowButton({ repCode }: { repCode: string }) {
  const mut = trpc.rep.retriggerIdentityWorkflow.useMutation({
    onSuccess: () => toast.success("Tag re-added — Wibiz Verify Identity workflow will re-trigger shortly."),
    onError: (e: any) => toast.error(e.message),
  });
  return (
    <button
      onClick={() => { if (confirm(`Re-add 'pending-identity-verification' tag for ${repCode}? This will re-enroll them in the Verify Identity workflow.`)) mut.mutate({ repCode }); }}
      disabled={mut.isPending}
      title="Re-trigger the Wibiz Verify Identity workflow for this associate"
      className="inline-flex items-center gap-1 bg-orange-50 hover:bg-orange-100 text-orange-700 text-xs px-2.5 py-1.5 rounded-lg font-medium transition-colors border border-orange-200 disabled:opacity-50"
    >
      {mut.isPending
        ? <div className="w-3 h-3 border border-orange-400 border-t-transparent rounded-full animate-spin" />
        : <RefreshCw className="w-3 h-3" />}
      Re-trigger
    </button>
  );
}

// ─── Self-Registration Requests Table ─────────────────────────────────────────
function SelfRegTable({ requests, onApprove, onReject, isPending }: {
  requests: any[];
  onApprove: (id: number) => void;
  onReject: (id: number) => void;
  isPending: boolean;
}) {
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const copyText = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(key);
    setTimeout(() => setCopiedId(null), 1500);
  };
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-100">
            <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Name</th>
            <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Email</th>
            <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Referred By</th>
            <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Rep Code</th>
            <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Verify Link</th>
            <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">W-9</th>
            <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Agreement</th>
            <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
            <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Submitted</th>
            <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Action</th>
          </tr>
        </thead>
        <tbody>
          {requests.map((req: any) => (
            <tr key={req.id} className="border-b border-gray-50 hover:bg-gray-50/50">
              <td className="px-4 py-3 font-medium text-gray-800 whitespace-nowrap">{req.firstName} {req.lastName}</td>
              <td className="px-4 py-3 text-gray-500 text-xs">{req.email}</td>
              <td className="px-4 py-3">
                {req.referredByRepCode ? (
                  <span className="font-mono text-xs bg-[#84ab4a]/10 text-[#84ab4a] px-2 py-0.5 rounded">{req.referredByRepCode}</span>
                ) : (
                  <span className="text-gray-300 text-xs">Direct</span>
                )}
              </td>
              <td className="px-4 py-3">
                {req.repCode ? (
                  <div className="flex items-center gap-1">
                    <span className="font-mono text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded font-semibold">{req.repCode}</span>
                    <button onClick={() => copyText(req.repCode, `rc-${req.id}`)} className="text-gray-400 hover:text-gray-600">
                      {copiedId === `rc-${req.id}` ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
                    </button>
                  </div>
                ) : (
                  <span className="text-gray-300 text-xs italic">Pending</span>
                )}
              </td>
              <td className="px-4 py-3">
                {req.verifyLink ? (
                  <div className="flex items-center gap-1">
                    <a href={req.verifyLink} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline flex items-center gap-0.5">
                      <Link2 className="w-3 h-3" /> Open
                    </a>
                    <button onClick={() => copyText(req.verifyLink, `vl-${req.id}`)} className="text-gray-400 hover:text-gray-600">
                      {copiedId === `vl-${req.id}` ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
                    </button>
                  </div>
                ) : (
                  <span className="text-gray-300 text-xs italic">Pending</span>
                )}
              </td>
              <td className="px-4 py-3">
                {req.w9DocUrl ? (
                  <a href={req.w9DocUrl.startsWith('http') ? `/api/files/ext:${encodeURIComponent(req.w9DocUrl)}` : `/api/files/${req.w9DocUrl}`} target="_blank" rel="noopener noreferrer" className="text-xs text-[#1A6FD4] underline flex items-center gap-1">
                    <Link2 className="w-3 h-3" /> View W-9
                  </a>
                ) : (
                  <span className="text-gray-300 text-xs italic">Not submitted</span>
                )}
              </td>
              {/* Signed Platform Participation Agreement. View opens the signed
                  copy (which carries its own print-to-PDF button); Save forces a
                  download so an admin can send a copy on. */}
              <td className="px-4 py-3">
                {req.agreementDocUrl ? (
                  <div className="flex items-center gap-2">
                    <a
                      href={`/api/files/${req.agreementDocUrl}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-[#1A6FD4] underline flex items-center gap-1"
                    >
                      <Link2 className="w-3 h-3" /> View
                    </a>
                    <a
                      href={`/api/files/${req.agreementDocUrl}?download=1`}
                      className="text-xs text-gray-400 hover:text-gray-600 underline"
                    >
                      Save
                    </a>
                  </div>
                ) : req.agreementAccepted ? (
                  // Accepted before signatures existed: a real acceptance, but
                  // there is no document to hand over. Say that rather than
                  // showing a dead link.
                  <span className="text-amber-600 text-xs" title="Accepted before signed agreements were introduced">
                    Accepted, unsigned
                  </span>
                ) : (
                  <span className="text-gray-300 text-xs italic">Not signed</span>
                )}
              </td>
              <td className="px-4 py-3">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  req.status === "pending" ? "bg-amber-100 text-amber-700" :
                  req.status === "approved" ? "bg-green-100 text-green-700" :
                  "bg-red-100 text-red-700"
                }`}>{req.status}</span>
              </td>
              <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">{new Date(req.createdAt).toLocaleDateString()}</td>
              <td className="px-4 py-3 text-right">
                {req.status === "pending" && (
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={() => onApprove(req.id)}
                      disabled={isPending}
                      className="bg-green-50 hover:bg-green-100 text-green-700 text-xs px-2.5 py-1 rounded-lg border border-green-200 font-medium transition-colors disabled:opacity-50"
                    >Approve</button>
                    <button
                      onClick={() => onReject(req.id)}
                      disabled={isPending}
                      className="bg-red-50 hover:bg-red-100 text-red-700 text-xs px-2.5 py-1 rounded-lg border border-red-200 font-medium transition-colors disabled:opacity-50"
                    >Reject</button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Self-Registration Requests Section ───────────────────────────────────────
function SelfRegistrationRequests() {
  const [expanded, setExpanded] = useState(false);
  const requestsQuery = trpc.agentSelfReg.list.useQuery();
  const updateStatusMutation = trpc.agentSelfReg.updateStatus.useMutation({
    onSuccess: () => { requestsQuery.refetch(); toast.success("Request updated"); },
    onError: (e: any) => toast.error(e.message),
  });

  const requests = requestsQuery.data ?? [];
  const pendingCount = requests.filter((r: any) => r.status === "pending").length;

  return (
    <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <UserPlus className="w-4 h-4 text-[#84ab4a]" />
          <span className="font-semibold text-gray-800 text-sm">Self-Registration Requests</span>
          {pendingCount > 0 && (
            <span className="bg-amber-100 text-amber-700 text-xs font-bold px-2 py-0.5 rounded-full">{pendingCount} pending</span>
          )}
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      {expanded && (
        <div className="border-t border-gray-100">
          {requests.length === 0 ? (
            <div className="py-8 text-center text-gray-400 text-sm">
              <UserPlus className="w-7 h-7 mx-auto mb-2 opacity-30" />
              No self-registration requests yet
            </div>
          ) : (
<SelfRegTable requests={requests} onApprove={(id) => updateStatusMutation.mutate({ id, status: "approved" })} onReject={(id) => updateStatusMutation.mutate({ id, status: "rejected" })} isPending={updateStatusMutation.isPending} />
          )}
        </div>
      )}
    </div>
  );
}

const STATUS_BADGE: Record<string, { label: string; color: string; bg: string }> = {
  not_submitted: { label: "Not Submitted", color: "text-gray-600", bg: "bg-gray-100" },
  pending: { label: "Pending Review", color: "text-yellow-700", bg: "bg-yellow-100" },
  verified: { label: "Verified", color: "text-green-700", bg: "bg-green-100" },
  rejected: { label: "Rejected", color: "text-red-700", bg: "bg-red-100" },
};

interface ReviewModalProps {
  rep: any;
  onClose: () => void;
  onRefresh: () => void;
}

function ReviewModal({ rep, onClose, onRefresh }: ReviewModalProps) {
  const [action, setAction] = useState<"approve" | "reject" | null>(null);
  const [rejectedReason, setRejectedReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const adminReview = trpc.identity.adminReview.useMutation();

  async function handleSubmit() {
    if (!action) return;
    if (action === "reject" && !rejectedReason.trim()) {
      setError("Please provide a reason for rejection");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await adminReview.mutateAsync({
        repId: rep.id,
        action,
        reviewerName: "Admin",
        rejectedReason: action === "reject" ? rejectedReason.trim() : undefined,
      });
      onRefresh();
      onClose();
    } catch (err: any) {
      setError(err.message ?? "Review failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#1a2744] to-[#2d3f6e] px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#84ab4a]" />
              <h2 className="text-white font-semibold">Identity Review</h2>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors text-xl leading-none">&times;</button>
          </div>
          <p className="text-gray-300 text-sm mt-1">{rep.firstName} {rep.lastName} · {rep.repCode}</p>
        </div>

        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Rep Info */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gray-50 rounded-xl p-3">
              <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
                <User className="w-3 h-3" /> Legal Name
              </div>
              <p className="text-sm font-medium text-gray-800">{rep.legalFullName || <span className="text-gray-400 italic">Not provided</span>}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3">
              <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
                <MapPin className="w-3 h-3" /> Address
              </div>
              <p className="text-sm font-medium text-gray-800">{rep.residentialAddress || <span className="text-gray-400 italic">Not provided</span>}</p>
            </div>
          </div>

          {/* Driver License */}
          <div>
            <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-2">
              <FileImage className="w-3 h-3" /> Driver License (Front)
            </div>
            {((rep as any).driverLicenseFrontKey || rep.driverLicenseFrontUrl) ? (
              <div className="relative group">
                <img
                  src={(rep as any).driverLicenseFrontKey ? `/api/files/${(rep as any).driverLicenseFrontKey}` : rep.driverLicenseFrontUrl!}
                  alt="Driver license"
                  className="w-full max-h-48 object-contain rounded-xl border border-gray-200 bg-gray-50"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
                <a
                  href={(rep as any).driverLicenseFrontKey ? `/api/files/${(rep as any).driverLicenseFrontKey}` : rep.driverLicenseFrontUrl!}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute top-2 right-2 bg-white/90 hover:bg-white text-gray-700 text-xs px-2 py-1 rounded-lg shadow flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Eye className="w-3 h-3" /> Open
                </a>
              </div>
            ) : (
              <div className="bg-gray-50 border border-dashed border-gray-300 rounded-xl p-6 text-center">
                <FileImage className="w-8 h-8 text-gray-300 mx-auto mb-1" />
                <p className="text-sm text-gray-400">No document uploaded</p>
              </div>
            )}
          </div>

          {/* Previous rejection reason */}
          {rep.identityRejectedReason && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3">
              <p className="text-xs text-red-600 font-medium mb-1">Previous Rejection Reason:</p>
              <p className="text-sm text-red-700">{rep.identityRejectedReason}</p>
            </div>
          )}

          {/* Action Selection */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Decision</p>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setAction("approve")}
                className={`flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl border-2 transition-all text-sm font-medium ${
                  action === "approve"
                    ? "border-green-500 bg-green-50 text-green-700"
                    : "border-gray-200 text-gray-600 hover:border-green-300 hover:bg-green-50"
                }`}
              >
                <ThumbsUp className="w-4 h-4" /> Approve
              </button>
              <button
                onClick={() => setAction("reject")}
                className={`flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl border-2 transition-all text-sm font-medium ${
                  action === "reject"
                    ? "border-red-500 bg-red-50 text-red-700"
                    : "border-gray-200 text-gray-600 hover:border-red-300 hover:bg-red-50"
                }`}
              >
                <ThumbsDown className="w-4 h-4" /> Reject
              </button>
            </div>
          </div>

          {/* Rejection Reason */}
          {action === "reject" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rejection Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                value={rejectedReason}
                onChange={(e) => setRejectedReason(e.target.value)}
                placeholder="Explain why the verification was rejected..."
                rows={3}
                className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
              />
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Submit */}
          <button
            onClick={handleSubmit}
            disabled={!action || submitting}
            className={`w-full py-2.5 px-4 rounded-xl font-semibold text-sm transition-colors flex items-center justify-center gap-2 ${
              !action || submitting
                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                : action === "approve"
                ? "bg-green-600 hover:bg-green-700 text-white"
                : "bg-red-600 hover:bg-red-700 text-white"
            }`}
          >
            {submitting ? (
              <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Processing...</>
            ) : action === "approve" ? (
              <><CheckCircle className="w-4 h-4" /> Approve Verification</>
            ) : action === "reject" ? (
              <><XCircle className="w-4 h-4" /> Reject Verification</>
            ) : (
              "Select a decision"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export function IdentityVerificationPanel() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [reviewingRep, setReviewingRep] = useState<any | null>(null);

  const { data: allReps, refetch } = trpc.rep.list.useQuery();

  const filtered = (allReps ?? []).filter((rep) => {
    const matchSearch =
      !search ||
      `${rep.firstName} ${rep.lastName} ${rep.repCode} ${rep.email}`
        .toLowerCase()
        .includes(search.toLowerCase());
    const matchStatus =
      statusFilter === "all" || rep.identityVerificationStatus === statusFilter;
    return matchSearch && matchStatus;
  });

  const pendingCount = (allReps ?? []).filter((r) => r.identityVerificationStatus === "pending").length;
  const verifiedCount = (allReps ?? []).filter((r) => r.identityVerificationStatus === "verified").length;
  const notSubmittedCount = (allReps ?? []).filter((r) => r.identityVerificationStatus === "not_submitted").length;

  return (
    <div className="space-y-5">
      {/* Stats Row */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: "Pending Review", count: pendingCount, color: "text-yellow-600", bg: "bg-yellow-50", icon: Clock },
          { label: "Verified", count: verifiedCount, color: "text-green-600", bg: "bg-green-50", icon: CheckCircle },
          { label: "Not Submitted", count: notSubmittedCount, color: "text-gray-500", bg: "bg-gray-50", icon: AlertCircle },
          { label: "Total Agents", count: (allReps ?? []).length, color: "text-blue-600", bg: "bg-blue-50", icon: User },
        ].map(({ label, count, color, bg, icon: Icon }) => (
          <div key={label} className={`${bg} rounded-xl p-3 flex items-center gap-3`}>
            <Icon className={`w-5 h-5 ${color} flex-shrink-0`} />
            <div>
              <p className={`text-xl font-bold ${color}`}>{count}</p>
              <p className="text-xs text-gray-500">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search agents..."
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#84ab4a]"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#84ab4a] bg-white"
        >
          <option value="all">All Statuses</option>
          <option value="pending">Pending Review</option>
          <option value="verified">Verified</option>
          <option value="rejected">Rejected</option>
          <option value="not_submitted">Not Submitted</option>
        </select>
      </div>

      {/* Agents Table */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100">
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Agent</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Rep Code</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Legal Name</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Document</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-10 text-gray-400">
                  <Shield className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  No agents found
                </td>
              </tr>
            ) : (
              filtered.map((rep) => {
                const badge = STATUS_BADGE[rep.identityVerificationStatus ?? "not_submitted"] ?? STATUS_BADGE.not_submitted;
                return (
                  <tr key={rep.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-gray-800">{rep.firstName} {rep.lastName}</p>
                        <p className="text-xs text-gray-400">{rep.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded">{rep.repCode}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-sm">
                      {rep.legalFullName || <span className="text-gray-300 italic">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.color}`}>
                        {rep.identityVerificationStatus === "pending" && <Clock className="w-3 h-3" />}
                        {rep.identityVerificationStatus === "verified" && <CheckCircle className="w-3 h-3" />}
                        {rep.identityVerificationStatus === "rejected" && <XCircle className="w-3 h-3" />}
                        {badge.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {((rep as any).driverLicenseFrontKey || rep.driverLicenseFrontUrl) ? (
                        <a
                          href={(rep as any).driverLicenseFrontKey ? `/api/files/${(rep as any).driverLicenseFrontKey}` : rep.driverLicenseFrontUrl!}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                        >
                          <Eye className="w-3 h-3" /> View
                        </a>
                      ) : (
                        <span className="text-xs text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2 flex-wrap">
                        {rep.identityVerificationStatus === "pending" ? (
                          <button
                            onClick={() => setReviewingRep(rep)}
                            className="bg-[#84ab4a] hover:bg-[#6d9038] text-white text-xs px-3 py-1.5 rounded-lg font-medium transition-colors"
                          >
                            Review
                          </button>
                        ) : rep.identityVerificationStatus !== "not_submitted" ? (
                          <button
                            onClick={() => setReviewingRep(rep)}
                            className="border border-gray-200 hover:border-gray-300 text-gray-600 text-xs px-3 py-1.5 rounded-lg transition-colors"
                          >
                            View Details
                          </button>
                        ) : (
                          <ResendVerifyLinkButton repCode={rep.repCode} />
                        )}
                        {(rep as any).ghlContactId && (
                          <RetriggerWorkflowButton repCode={rep.repCode} />
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Self-Registration Requests */}
      <SelfRegistrationRequests />

      {/* Review Modal */}
      {reviewingRep && (
        <ReviewModal
          rep={reviewingRep}
          onClose={() => setReviewingRep(null)}
          onRefresh={() => { refetch(); setReviewingRep(null); }}
        />
      )}
    </div>
  );
}
